// Trusts `attrs.encoded_id` to be present — the backend must include it.
// Throws loudly if it's missing rather than silently producing a record with

import { capitalize } from "lodash";
import moment from 'moment';
import { formatDate } from "utils/helpers";

// an empty/unsafe id that would propagate into URLs and action calls.
export const mapAppointment = (attrs: any) => {
  const duration = Number(attrs.duration ?? 0);
  const start = moment.parseZone(attrs.appointment_at);
  const hasValidDate = start.isValid();
  const end = hasValidDate ? start.clone().add(duration, 'minutes') : start;
  const convAttrs = attrs?.latest_conversation?.data?.attributes;
  if (!attrs.encoded_id) {
    throw new Error('Appointment is missing id — cannot safely render or act on it.');
  }
  return {
    id: String(attrs.encoded_id),
    appointmentAt: hasValidDate ? attrs.appointment_at : undefined,
    date: hasValidDate ? formatDate(start) : '',
    startTime: hasValidDate ? formatDate(start, 'twelveHour') : '',
    endTime: hasValidDate ? formatDate(end, 'twelveHour') : '',
    durationMinutes: duration,
    type: attrs.type ?? attrs.appointment_type,
    provider: buildProvider(attrs),
    status: String(attrs.status ?? ''),
    confirmed: Boolean(attrs.confirmed),
    notes: attrs.notes ?? undefined,
    bookedAt: attrs.created_at ?? undefined,
    requests: buildRequests(attrs),
    latestConversation: convAttrs?.encoded_id
      ? {
        encodedId: convAttrs.encoded_id,
        title: convAttrs.title ?? '',
        state: convAttrs.state,
        lastMessage: (() => {
          const msgAttrs = convAttrs?.last_non_internal_message?.data?.attributes;
          return msgAttrs ? { content: msgAttrs.content, fromPatient: msgAttrs.from_patient, time: msgAttrs.time } : undefined;
        })(),
      }
      : undefined,
  };
};

// Extract provider name + role from the JSON:API-nested provider payload.
// Backend shape: attrs.provider.data.attributes = { first_name, last_name, role }
const buildProvider = (attrs: any): { name: string; role: string } => {
  const providerAttrs = attrs?.provider?.data?.attributes;
  if (!providerAttrs) return { name: '', role: '' };
  const name = [providerAttrs.first_name, providerAttrs.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return { name, role: capitalize(String(providerAttrs.role ?? '')) };
};

// Pull AppointmentRequests off the JSON:API-nested collection. Backend shape:
// attrs.appointment_requests.data[i].attributes = { action_type, state, created_at, ... }
// Returned chronologically (oldest → newest) so the UI can render a timeline
// without re-sorting.
const buildRequests = (attrs: any) => {
  const list: any[] = attrs?.appointment_requests?.data ?? [];
  const mapped = list
    .map(requestMapper)
    .filter((r) => r !== null);

  return mapped.sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
  });
};


export function requestMapper(entry) {
  const a = entry?.attributes;
  if (!a) return null;
  return {
    id: String(entry.id ?? ''),
    actionType: String(a.action_type ?? ''),
    state: String(a.state ?? ''),
    createdAt: a.created_at ?? '',
    source: a.source ?? undefined,
    proposedAt: a.appointment_at ?? undefined,
    proposedDuration: a.duration != null ? Number(a.duration) : undefined,
  };

}
